import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ParticipantService } from '../../../services/participant.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { DocumentTypeEnum } from '../../../enums/DocumentTypeEnum';
import { ParticipantStatus, PARTICIPANT_STATUS_OPTIONS, getParticipantStatusLabel } from '../../../enums/ParticipantStatus';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-new-participant',
  templateUrl: './new-participant.component.html',
  styleUrl: './new-participant.component.scss'
})
export class NewParticipantComponent implements OnInit {

  participantForm!: FormGroup;
  participantId: any = null;
  documentTypes = Object.entries(DocumentTypeEnum).map(([key, value]) => ({ key, value }));
  statusOptions = PARTICIPANT_STATUS_OPTIONS;
  ParticipantStatus = ParticipantStatus;

  // Flag to show/hide auto-generate button (only in non-production environments)
  isProductionEnvironment = environment.environmentName === 'production';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private _participantSercice: ParticipantService,
    private toastService: ToastService,
    private _loadingService: LoadingService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id && id !== 'new') {
        this.participantId = Number(id);
        this.getParticipantById(this.participantId);
      }
    });
  }

  private initializeForm(): void {
    this.participantForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      documentId: ['', Validators.required],
      typeDocument: ['', Validators.required],
      expeditionPlace: ['', Validators.required],
      profession: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      cellPhone: ['', Validators.required],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      city: ['', Validators.required],
      country: ['', Validators.required],
      age: [''],
      company: [''],
      referenceName: ['', Validators.required],
      referenceRelationship: ['', Validators.required],
      referencePhoneNumber: ['', Validators.required],
      status: [''] // Status is optional, can be set later
    });
  }

  getParticipantById(id: number) {
    if (!id || isNaN(id)) {
      this.toastService.showToast('error', 'ID de participante inválido');
      return;
    }

    this._loadingService.show();
    this._participantSercice.getById(id).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.participantForm.patchValue({
            firstName: response.data.firstName || '',
            lastName: response.data.lastName || '',
            documentId: response.data.documentId || '',
            typeDocument: response.data.typeDocument || '',
            expeditionPlace: response.data.expeditionPlace || '',
            profession: response.data.profession || '',
            email: response.data.email || '',
            phone: response.data.phone || '',
            cellPhone: response.data.cellPhone || '',
            addressLine1: response.data.addressLine1 || '',
            addressLine2: response.data.addressLine2 || '',
            city: response.data.city || '',
            country: response.data.country || '',
            age: response.data.age || '',
            referenceName: response.data.referenceName || '',
            referenceRelationship: response.data.referenceRelationship || '',
            referencePhoneNumber: response.data.referencePhoneNumber || '',
            status: response.data.status || ''
          });
        }
        this._loadingService.hide();
      },
      error: (e) => {
        console.error("Error cargando participante:", e);
        this.toastService.showToast('error', 'Error al cargar los datos del participante');
        this._loadingService.hide();
      }
    });
  }

  onSubmit() {
    this._loadingService.show();
    const formValue = this.participantForm.value;
    const newParticipant = {
      ...formValue,
      age: Number(formValue.age) || 0, // Asegurarse de que age es un número
      status: formValue.status && formValue.status !== '' ? Number(formValue.status) : null // Convertir status a número o null si está vacío
    };
    if (this.participantForm.valid) {
      if (this.participantId) {
        // Actualizar estudiante existente
        this._participantSercice.update(this.participantId, newParticipant).subscribe({
          next: () => {
            this.handleSuccess('Estudiante actualizado correctamente.');
          },
          error: (error) => {
            const errorMessage = error?.error?.errorMessage || error?.error?.message || 'Se produjo un error al actualizar el participante.';
            this.handleError(errorMessage);
          }
        });
      } else {
        // Crear nuevo estudiante
        this._participantSercice.insert(newParticipant).subscribe({
          next: () => {
            this.handleSuccess('Estudiante creado correctamente.');
          },
          error: (error) => {
            const errorMessage = error?.error?.errorMessage || error?.error?.message || 'Se produjo un error al crear el participante.';
            this.handleError(errorMessage);
          }
        });
      }
    }
  }

  private handleSuccess(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('success', message);
    this.router.navigate(['/participants']);
  }

  private handleError(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('error', message);
  }

  goBack(): void {
    this.router.navigate(['/participants']);
  }

  // Helper method for getting status label
  getStatusLabel(status: any): string {
    return getParticipantStatusLabel(status);
  }

  /**
   * Generates random participant data for testing purposes.
   * Only available in non-production environments.
   */
  generateRandomParticipant(): void {
    const firstNames = ['Juan', 'María', 'Carlos', 'Ana', 'Pedro', 'Laura', 'Diego', 'Sofía', 'Andrés', 'Valentina', 'Miguel', 'Camila', 'José', 'Isabella', 'David'];
    const lastNames = ['García', 'Rodríguez', 'Martínez', 'López', 'González', 'Hernández', 'Pérez', 'Sánchez', 'Ramírez', 'Torres', 'Flores', 'Rivera', 'Gómez', 'Díaz', 'Reyes'];
    const cities = ['Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Bucaramanga', 'Pereira', 'Santa Marta', 'Manizales', 'Ibagué'];
    const professions = ['Ingeniero', 'Médico', 'Abogado', 'Contador', 'Arquitecto', 'Administrador', 'Psicólogo', 'Economista', 'Enfermero', 'Docente'];
    const relationships = ['Padre', 'Madre', 'Cónyuge', 'Hermano', 'Hermana', 'Tío', 'Tía', 'Amigo', 'Primo', 'Abuelo'];
    const documentTypeKeys = Object.keys(DocumentTypeEnum).filter(key => isNaN(Number(key)));

    const randomElement = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
    const randomNumber = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min;
    const randomDocumentId = (): string => String(randomNumber(10000000, 99999999));
    const randomPhone = (): string => `3${randomNumber(0, 2)}${randomNumber(0, 9)}${randomNumber(1000000, 9999999)}`;

    const firstName = randomElement(firstNames);
    const lastName = randomElement(lastNames);
    const city = randomElement(cities);

    this.participantForm.patchValue({
      firstName: firstName,
      lastName: lastName,
      documentId: randomDocumentId(),
      typeDocument: randomElement(documentTypeKeys),
      expeditionPlace: city,
      profession: randomElement(professions),
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}${randomNumber(1, 999)}@test.com`,
      phone: `(1) ${randomNumber(100, 999)} ${randomNumber(1000, 9999)}`,
      cellPhone: randomPhone(),
      addressLine1: `Calle ${randomNumber(1, 150)} # ${randomNumber(1, 100)}-${randomNumber(1, 100)}`,
      addressLine2: randomNumber(0, 1) === 1 ? `Apto ${randomNumber(101, 1500)}` : '',
      city: city,
      country: 'Colombia',
      age: randomNumber(18, 65),
      referenceName: `${randomElement(firstNames)} ${randomElement(lastNames)}`,
      referenceRelationship: randomElement(relationships),
      referencePhoneNumber: randomPhone()
    });

    this.toastService.showToast('success', 'Datos de prueba generados correctamente');
  }

}
